import PageShell from '@/pages/shared/PageShell';

export default function TeamCollaboration() {
  return (
    <PageShell
      title="チーム機能｜チャット・タスク・ファイルを一元管理｜”おもいむすび”"
      description="プロジェクト別チャット、タスク別コメント、メンション通知、ファイル共有、プロジェクト横断検索。プロジェクトに必要なコミュニケーション機能を一元化します。"
      keywords="プロジェクト管理 チャット,チーム コラボレーション,プロジェクト管理 情報共有,プロジェクト管理 コミュニケーション,タスク管理 チーム"
      canonical="https://omoimusubi.asia/features/team-collaboration/"
      h1="チャット、ファイル、タスクをひとつに"
      subtitle="プロジェクトに必要なコミュニケーション機能をすべて”おもいむすび”上で完結させ、情報の分散を防ぎます。"
      relatedLinks={[
        { label: 'AIファイル管理', path: '/features/ai-file-management/' },
        { label: 'AI日報', path: '/features/ai-daily-report/' },
        { label: 'プロジェクト情報共有ガイド', path: '/guides/project-information-sharing/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20scene%20with%20three%20Japanese%20team%20members%20a%20man%20and%20two%20women%20in%20their%2030s%20collaborating%20around%20a%20white%20table%20with%20laptops%20open%20they%20are%20smiling%20and%20discussing%20work%20together%20one%20person%20pointing%20at%20a%20screen%20showing%20project%20chat%20messages%20and%20task%20cards%20natural%20daylight%20streaming%20through%20floor%20to%20ceiling%20windows%20green%20indoor%20plants%20modern%20minimalist%20Japanese%20interior%20refreshing%20clean%20energetic%20atmosphere%20genuine%20teamwork&width=1200&height=700&seq=feature-team-collaboration-01&orientation=landscape&nocache=true"
          alt="チーム機能の製品画面 - チャット・タスク・ファイルがひとつに統合されたワークスペース"
          className="w-full h-auto"
        />
      </div>

      <section>
        <h2>チーム機能一覧</h2>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          {[
            { icon: 'ri-chat-3-line', title: 'プロジェクト別チャット', desc: 'プロジェクトごとにチャットルームを自動作成。タスクに関する会話をプロジェクト内で完結させ、情報の分散を防ぎます。' },
            { icon: 'ri-message-2-line', title: 'タスク別コメント', desc: '各タスクに対してコメントを残せます。タスクに関する議論が分散せず、後から文脈を追いやすくなります。' },
            { icon: 'ri-group-line', title: 'メンバー管理', desc: 'プロジェクトへの参加メンバーを管理し、役割（管理者、編集者、閲覧者）を設定できます。' },
            { icon: 'ri-shield-user-line', title: '権限管理', desc: 'メンバーごとに閲覧権限、編集権限を細かく設定。機密性の高いプロジェクトでも安全に運用できます。' },
            { icon: 'ri-user-smile-line', title: 'オンライン状況', desc: 'メンバーのオンライン状況を表示。リモートや海外拠点のメンバーとのコミュニケーションをスムーズにします。' },
            { icon: 'ri-at-line', title: 'メンション', desc: '@メンションで特定のメンバーに通知を送れます。チャットでもタスクコメントでも利用可能です。' },
            { icon: 'ri-notification-3-line', title: '通知', desc: 'タスク更新、コメント追加、期限接近、メンションなどをリアルタイムで通知します。' },
            { icon: 'ri-folder-shared-line', title: 'ファイル共有', desc: 'プロジェクト内でファイルを共有し、AI検索の対象にできます。ドラッグ＆ドロップで簡単にアップロード。' },
            { icon: 'ri-search-line', title: 'プロジェクト横断検索', desc: '複数プロジェクトにまたがって、タスク、ファイル、チャットを横断的に検索できます。' },
            { icon: 'ri-pages-line', title: '情報の一元管理', desc: 'タスク、チャット、ファイル、日報がすべて”おもいむすび”上に集約。情報のサイロ化を防ぎます。' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="w-9 h-9 bg-accent-100 rounded-lg flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-accent-600 text-sm`}></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>”おもいむすび”が目指すコミュニケーション</h2>
        <p>プロジェクト管理において、コミュニケーションは最も重要な要素の一つです。しかし、多くのチームでは以下のような問題に直面しています。</p>
        <ul>
          <li>タスクの話はプロジェクト管理ツール、雑談はチャットツール、ファイル共有は別のツール...と情報が分散</li>
          <li>「この話、どこでしてたっけ？」という状態が日常的に発生</li>
          <li>新しいメンバーが参加したときに、過去の経緯を追うのが困難</li>
        </ul>
        <p>”おもいむすび”は、プロジェクトに必要なすべてのコミュニケーションを一つのプラットフォームに統合します。タスクの議論はタスクのコメントで、プロジェクト全体の連絡はプロジェクトチャットで。すべてが検索可能な状態で保存され、情報の散逸を防ぎます。</p>
      </section>

      <section>
        <h2>通知設定のカスタマイズ</h2>
        <p>通知の頻度や種類は、メンバーごとにカスタマイズ可能です。必要な情報だけを受け取り、通知疲れを防ぎます。</p>
        <ul>
          <li><strong>メール通知</strong>：重要な更新をメールでも受け取れます</li>
          <li><strong>アプリ内通知</strong>：リアルタイムで通知を表示</li>
          <li><strong>ダイジェスト通知</strong>：1日のまとめを指定時間に受信</li>
          <li><strong>ミュート設定</strong>：特定のプロジェクトやタスクの通知を一時停止</li>
        </ul>
      </section>
    </PageShell>
  );
}